import * as Sentry from "@sentry/react"
import { types, flow, Instance, SnapshotOut } from "mobx-state-tree"

import {
  PICKER_INTENTS,
  pickerIntentForStatus,
  type PickerIntent,
} from "@/components/b2cReschedule/pickerIntent"
import {
  validateAndFetchOrder,
  getOrderByToken,
  getAllLocations,
  HubLocation,
  rescheduleB2cOrder as rescheduleB2cOrderApi,
  cancelB2cOrder as cancelB2cOrderApi,
} from "@/services/firebase"
import {
  PusherService,
  LocationUpdate,
  StageUpdate,
  DriverStatusUpdate,
  LiveETAUpdate,
} from "@/services/pusher"
import { decodePolyline, snapLocationToRoute, shouldRecalculateRoute } from "@/utils/routeHelpers"

import { isCurrentB2cMutation, startB2cMutation } from "./b2cMutationScope"
import { OrderModel, LocationModel, RouteModel } from "./Order"

// Hub location model for all locations display
const AllLocationModel = types.model("AllLocation", {
  id: types.string,
  location: types.string,
  address: types.string,
  latitude: types.number,
  longitude: types.number,
  city: types.string,
  street: types.maybe(types.string),
  number: types.maybe(types.string),
  postcode: types.maybe(types.string),
  openingHours: types.maybe(types.frozen()),
})

/**
 * Keeps Sentry's persistent scope in step with the tracked order (PLA-633).
 *
 * `Sentry.setTag` writes isolation-scope state that outlives the store
 * action that set it, so the tag has to be overwritten whenever a different
 * order is loaded and cleared whenever tracking resets — otherwise errors
 * raised anywhere else in the SPA, or on a later legacy
 * `/track/{orderId}/{trackingToken}` route, would still be attributed to the
 * previous order. Order id only, never the tracking token (a bearer
 * credential; a token in Sentry context would be a credential leak).
 * `undefined` clears the tag — Sentry's Scope has no removeTag.
 */
const syncSentryOrderTag = (orderId: string | undefined) => {
  Sentry.setTag("orderId", orderId)
}

export const OrderTrackingStoreModel = types
  .model("OrderTrackingStore", {
    currentOrder: types.maybe(OrderModel),
    currentOrderId: types.maybe(types.string),
    trackingToken: types.maybe(types.string),
    isLoading: types.optional(types.boolean, false),
    isSyncing: types.optional(types.boolean, false),
    retryAttempt: types.optional(types.number, 0),
    maxRetriesReached: types.optional(types.boolean, false),
    error: types.maybe(types.string),
    isConnected: types.optional(types.boolean, true),
    lastUpdateTime: types.maybe(types.Date),
    decodedRoutePoints: types.optional(types.array(LocationModel), []),
    routePolyline: types.maybe(types.string),
    routeDistance: types.maybe(types.number),
    routeDuration: types.maybe(types.string),
    lastRouteUpdate: types.optional(types.Date, () => new Date()),
    isDriverOffRoute: types.optional(types.boolean, false),
    isPusherConnected: types.optional(types.boolean, false),
    showRescheduleModal: types.optional(types.boolean, false),
    hasShownNoShowModal: types.optional(types.boolean, false),
    // All locations for initial display
    allLocations: types.optional(types.array(AllLocationModel), []),
    showAllLocations: types.optional(types.boolean, false),
    isLoadingLocations: types.optional(types.boolean, false),
    // Countdown timer states
    isCountdownActive: types.optional(types.boolean, false),
    countdownComplete: types.optional(types.boolean, false),
    // Track if user came from success page (for delayed resync)
    cameFromSuccessPage: types.optional(types.boolean, false),
    // B2C reschedule/cancel modal states
    showB2cRescheduleModal: types.optional(types.boolean, false),
    showB2cCancelModal: types.optional(types.boolean, false),
    // PLA-878: the B2C picker doubles as the cancel entry point ("cancel" intent)
    b2cPickerIntent: types.optional(
      types.enumeration<PickerIntent>("B2cPickerIntent", [...PICKER_INTENTS]),
      "reschedule",
    ),
    rescheduleSucceededB2c: types.optional(types.boolean, false),
    isReschedulingB2c: types.optional(types.boolean, false),
    isCancellingB2c: types.optional(types.boolean, false),
    rescheduleErrorB2c: types.maybe(types.string),
    // Callable code of the failed reschedule (PLA-881): slot-taken vs transient recovery
    rescheduleErrorCodeB2c: types.maybe(types.string),
    cancelErrorB2c: types.maybe(types.string),
    isPollingAfterAction: types.optional(types.boolean, false),
  })
  .volatile(() => ({
    pusherService: null as PusherService | null,
    serviceTypePollingInterval: null as ReturnType<typeof setInterval> | null,
    syncRetryInterval: null as ReturnType<typeof setInterval> | null,
    delayedSyncTimeout: null as ReturnType<typeof setTimeout> | null,
    b2cActionPollingInterval: null as ReturnType<typeof setTimeout> | null,
    b2cActionPollingAttempt: 0,
    // PLA-878: scopes a reschedule outcome to the picker session that submitted it
    rescheduleRequestId: 0,
    rescheduleInFlight: 0,
    cancelInFlight: 0,
    confirmedSlot: null as { abholdatum: string; spatesteAbholung: string } | null,
  }))
  .views((self) => ({
    get hasOrder() {
      return !!self.currentOrder
    },
    get customerLocation() {
      if (!self.currentOrder) return null

      // First check if we have an explicit customer location from backend
      const customerLoc = self.currentOrder.customerLocation
      if (customerLoc) {
        return {
          latitude: customerLoc.latitude,
          longitude: customerLoc.longitude,
        }
      }

      // Fallback to hub location as customer location for pickup/delivery service
      const hubLoc = self.currentOrder.hubLocation
      if (hubLoc) {
        return {
          latitude: hubLoc.latitude,
          longitude: hubLoc.longitude,
        }
      }

      return null
    },
    get pickupLocation() {
      if (!self.currentOrder) return null
      // Use hub location as pickup location
      const hubLoc = self.currentOrder.hubLocation
      // Getting pickup location from hub location
      if (hubLoc) {
        const location = {
          latitude: hubLoc.latitude,
          longitude: hubLoc.longitude,
        }
        // Returning pickup location coordinates
        return location
      }
      // No fallback - return null if no hub location
      return null
    },
    get routeToCustomer() {
      return self.currentOrder?.route || null
    },
    get snappedDriverLocation() {
      if (!self.currentOrder?.currentDriverLocation || !self.currentOrder?.isLiveTrackingPhase) {
        return self.currentOrder?.currentDriverLocation || null
      }

      // If no route points, return raw location
      if (self.decodedRoutePoints.length === 0) {
        return self.currentOrder.currentDriverLocation
      }

      // Apply snapping logic
      const driverLoc = self.currentOrder.currentDriverLocation
      const pickupLoc = (self as any).pickupLocation

      if (!pickupLoc) {
        return driverLoc
      }

      const routePoints = self.decodedRoutePoints.map((p) => ({
        latitude: p.latitude,
        longitude: p.longitude,
      }))

      const snappedLocation = snapLocationToRoute(driverLoc, routePoints, pickupLoc)

      return {
        ...driverLoc,
        latitude: snappedLocation.latitude,
        longitude: snappedLocation.longitude,
      }
    },
    get needsRouteRecalculation() {
      if (!self.currentOrder?.currentDriverLocation || !self.currentOrder?.isLiveTrackingPhase) {
        return false
      }

      if (self.decodedRoutePoints.length === 0) {
        return true
      }

      const driverLoc = self.currentOrder.currentDriverLocation
      const routePoints = self.decodedRoutePoints.map((p) => ({
        latitude: p.latitude,
        longitude: p.longitude,
      }))

      return shouldRecalculateRoute(driverLoc, routePoints, self.lastRouteUpdate)
    },
  }))
  .actions((self) => ({
    loadOrder: flow(function* (orderId: string, trackingToken?: string) {
      self.isLoading = true
      self.isSyncing = true
      self.error = undefined
      self.currentOrderId = orderId
      self.trackingToken = trackingToken || undefined
      syncSentryOrderTag(orderId)

      try {
        // Always require a tracking token for real data
        if (trackingToken) {
          // Real backend call with tracking token
          const orderData = yield validateAndFetchOrder(orderId, trackingToken)

          // Received order data from backend with tracking info

          // Validate serviceType before creating order - prevent undefined
          const validServiceType =
            orderData.serviceType === "pickup_delivery" || orderData.serviceType === "driveby"
              ? orderData.serviceType
              : "driveby" // Default to driveby if invalid or missing (2-stage is safer)

          if (orderData.serviceType !== validServiceType) {
            // Invalid or missing serviceType, defaulting to driveby
          }

          // Create order with all required fields, then update from backend
          self.currentOrder = OrderModel.create({
            id: orderData.id,
            numberPlate: orderData.numberPlate,
            customerName: orderData.customerName,
            status: orderData.status || orderData.orderPhase,
            orderPhase: orderData.orderPhase,
            serviceType: validServiceType,
            hubLocation: orderData.hubLocation || undefined,
            customerLocation: orderData.customerLocation || undefined,
            currentDriverLocation: orderData.currentDriverLocation || undefined,
            hasPdfReport: orderData.hasPdfReport || false,
            inspectionPassed:
              orderData.inspectionPassed === null ? undefined : orderData.inspectionPassed,
            trackingValid: orderData.trackingValid !== false,
            lastLocationUpdate: orderData.lastLocationUpdate
              ? new Date(orderData.lastLocationUpdate)
              : undefined,
            scheduledPickupTime: orderData.scheduledPickupTime || undefined,
            scheduledReturnTime: orderData.scheduledReturnTime || undefined,
            ticketRescheduling: orderData.ticketRescheduling || undefined,
            updatedAt: new Date(),
            createdAt: new Date(),
            estimatedArrival: undefined,
            route: undefined,
          })

          // Use updateFromBackend to properly map all fields
          if (self.currentOrder) {
            self.currentOrder.updateFromBackend(orderData)
          }
          self.lastUpdateTime = new Date()
          // Order loaded successfully - stop syncing
          self.isSyncing = false

          // Check if serviceType needs polling (undefined or null)
          if (!orderData.serviceType) {
            // Start polling for serviceType
            // @ts-ignore - Method exists but TypeScript can't infer it
            self.startServiceTypePolling()
          }

          // Connect to Pusher for real-time updates
          // @ts-ignore - Method exists but TypeScript can't infer it
          self.connectToPusher()

          // Schedule delayed resync only if user came from success page (1 minute delay)
          if (self.cameFromSuccessPage) {
            // @ts-ignore - Method exists but TypeScript can't infer it
            self.scheduleDelayedResync()
          }

          // Check if order is no_show or cancelled and show modal
          if (
            (orderData.status === "no_show" ||
              orderData.status === "cancelled" ||
              orderData.status === "709823443" ||
              orderData.status === "3552907480") &&
            !self.hasShownNoShowModal
          ) {
            // @ts-ignore - Method exists but TypeScript can't infer it
            self.openNoShowOrCancelledPicker(orderData.status, orderData.orderSource)
          }
        } else {
          throw new Error("Tracking token required for real orders")
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { flow: "tracking", operation: "load-order" },
        })
        self.error = error instanceof Error ? error.message : String(error)

        // If no order was loaded and we haven't reached max retries, start sync retry process
        // But only if we're not already in a retry cycle (prevent recursive calls)
        if (!self.currentOrder && !self.maxRetriesReached && !self.isSyncing) {
          // @ts-ignore - Method exists but TypeScript can't infer it
          self.startSyncRetrying()
        }
      } finally {
        self.isLoading = false
      }
    }),

    loadOrderByToken: flow(function* (trackingToken: string, isRetryAttempt: boolean = false) {
      // Validate that trackingToken is a string
      if (typeof trackingToken !== "string") {
        self.error = "Invalid tracking token format"
        self.isLoading = false
        return
      }

      self.isLoading = true
      // Only set isSyncing = true if this is NOT a retry attempt (retry logic manages this)
      if (!isRetryAttempt) {
        self.isSyncing = true
        // Start countdown timer when first loading
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.startCountdown()
      }
      self.error = undefined
      self.trackingToken = trackingToken

      try {
        // Fetch order by token only (backend will look up the order ID)
        const orderData = yield getOrderByToken(trackingToken)

        // Received order data via token authentication

        // Set the order ID from the fetched data
        self.currentOrderId = orderData.id

        // Non-PII correlation tag — see syncSentryOrderTag. PLA-633.
        syncSentryOrderTag(orderData.id)

        // Validate serviceType before creating order - prevent undefined
        const validServiceType =
          orderData.serviceType === "pickup_delivery" || orderData.serviceType === "driveby"
            ? orderData.serviceType
            : "driveby" // Default to driveby if invalid or missing (2-stage is safer)

        if (orderData.serviceType !== validServiceType) {
          // Invalid or missing serviceType, defaulting to driveby
        }

        // Create order with all required fields, then update from backend
        self.currentOrder = OrderModel.create({
          id: orderData.id,
          numberPlate: orderData.numberPlate,
          customerName: orderData.customerName,
          status: orderData.status || orderData.orderPhase,
          orderPhase: orderData.orderPhase,
          serviceType: validServiceType,
          hubLocation: orderData.hubLocation || undefined,
          customerLocation: orderData.customerLocation || undefined,
          currentDriverLocation: orderData.currentDriverLocation || undefined,
          hasPdfReport: orderData.hasPdfReport || false,
          inspectionPassed:
            orderData.inspectionPassed === null ? undefined : orderData.inspectionPassed,
          trackingValid: orderData.trackingValid !== false,
          lastLocationUpdate: orderData.lastLocationUpdate
            ? new Date(orderData.lastLocationUpdate)
            : undefined,
          scheduledPickupTime: orderData.scheduledPickupTime || undefined,
          scheduledReturnTime: orderData.scheduledReturnTime || undefined,
          ticketRescheduling: orderData.ticketRescheduling || undefined,
          updatedAt: new Date(),
          createdAt: new Date(),
          estimatedArrival: undefined,
          route: undefined,
        })

        // Use updateFromBackend to properly map all fields
        if (self.currentOrder) {
          self.currentOrder.updateFromBackend(orderData)
        }
        self.lastUpdateTime = new Date()
        // Order loaded successfully with token - stop syncing
        self.isSyncing = false
        // Stop countdown when order loads
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.stopCountdown()

        // Check if serviceType needs polling (undefined or null)
        if (!orderData.serviceType) {
          // Start polling for serviceType
          // @ts-ignore - Method exists but TypeScript can't infer it
          self.startServiceTypePolling()
        }

        // Connect to Pusher for real-time updates
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.connectToPusher()

        // Schedule delayed resync only if user came from success page (1 minute delay)
        if (self.cameFromSuccessPage) {
          // @ts-ignore - Method exists but TypeScript can't infer it
          self.scheduleDelayedResync()
        }

        // Check if order is no_show or cancelled and show modal
        if (
          (orderData.status === "no_show" ||
            orderData.status === "cancelled" ||
            orderData.status === "709823443" ||
            orderData.status === "3552907480") &&
          !self.hasShownNoShowModal
        ) {
          // @ts-ignore - Method exists but TypeScript can't infer it
          self.openNoShowOrCancelledPicker(orderData.status, orderData.orderSource)
        }
      } catch (error) {
        self.error = error instanceof Error ? error.message : String(error)

        // Only start sync retry process if this is NOT already a retry attempt
        if (!isRetryAttempt && !self.currentOrder && !self.maxRetriesReached) {
          // @ts-ignore - Method exists but TypeScript can't infer it
          self.startSyncRetrying()
        } else if (isRetryAttempt) {
          // Keep isSyncing = true, let retry logic handle it
        } else {
          self.isSyncing = false
        }
      } finally {
        self.isLoading = false
      }
    }),

    updateDriverLocation(location: Instance<typeof LocationModel>) {
      if (self.currentOrder) {
        self.currentOrder.updateDriverLocation(location)
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.updateLastUpdateTime()
      }
    },

    updateOrderStatus(status: number | string) {
      if (self.currentOrder) {
        // Convert numeric status to string if needed
        let stringStatus: string
        if (typeof status === "number") {
          // Handle numeric status (1-6)
          const numericToStatus: Record<number, string> = {
            1: "new",
            2: "confirmed",
            3: "picking_up",
            4: "at_hub",
            5: "returning",
            6: "completed",
          }
          stringStatus = numericToStatus[status] || "new"
        } else if (status === "709823443" || status === "3552907480") {
          // Handle no_show (709823443) or cancelled (3552907480) status codes from HubSpot
          stringStatus = status === "709823443" ? "no_show" : "cancelled"
        } else {
          // Use string status directly
          stringStatus = status
        }
        // Updating order status
        self.currentOrder.updateStatus(stringStatus)

        // Check if status is no_show or cancelled and show modal if not shown yet
        if (
          (stringStatus === "no_show" || stringStatus === "cancelled") &&
          !self.hasShownNoShowModal
        ) {
          // @ts-ignore - Method exists but TypeScript can't infer it
          self.openNoShowOrCancelledPicker(stringStatus, self.currentOrder.orderSource)
        }

        // @ts-ignore - Method exists but TypeScript can't infer it
        self.updateLastUpdateTime()
      }
    },

    // Silent refresh order data - called from Pusher events to fetch latest data
    silentRefreshOrderData: flow(function* () {
      try {
        // Only refresh if we have the necessary tokens and an existing order
        if (self.trackingToken && self.currentOrderId && self.currentOrder) {
          console.log("[STORE] Silent refresh: fetching latest order data")

          // Fetch latest order data
          const orderData = yield validateAndFetchOrder(self.currentOrderId, self.trackingToken)

          if (orderData) {
            console.log("[STORE] Silent refresh: updating order with new data")
            console.log("[STORE] hasPdfReport:", orderData.hasPdfReport)
            console.log("[STORE] inspectionPassed:", orderData.inspectionPassed)

            // Update existing order object - this triggers MobX reactivity
            self.currentOrder.updateFromBackend(orderData)
            self.lastUpdateTime = new Date()
          }
        }
      } catch (error) {
        console.error("[STORE] Silent refresh failed:", error)
        // Silently fail - don't disrupt user experience
      }
    }),

    // Named flow action for post-action polling refresh — must be a top-level
    // model action so MST's flow context is satisfied when called from setTimeout.
    _refreshAfterAction: flow(function* () {
      try {
        if (self.trackingToken && self.currentOrderId) {
          const orderData = yield validateAndFetchOrder(self.currentOrderId, self.trackingToken)
          if (self.currentOrder && orderData) {
            self.currentOrder.updateFromBackend(orderData)
          }
        }
      } catch {
        // Silently fail
      }
    }),

    setDriverOffRoute(offRoute: boolean) {
      // @ts-ignore - Method exists but TypeScript can't infer it
      self.setDriverOffRouteState(offRoute)
    },

    updateRoute(route: Instance<typeof RouteModel>) {
      if (self.currentOrder) {
        self.currentOrder.updateRoute(route)
      }
    },

    updateETA(eta: Date) {
      if (self.currentOrder) {
        self.currentOrder.updateETA(eta)
      }
    },

    setConnectionStatus(isConnected: boolean) {
      // @ts-ignore - Method exists but TypeScript can't infer it
      self.setConnectionStatusState(isConnected)
    },

    updateRouteData(polyline: string, distance: number, duration: string) {
      // @ts-ignore - Method exists but TypeScript can't infer it
      self.updateRouteDataState(polyline, distance, duration)

      // Decode polyline to route points
      if (polyline) {
        const decodedPoints = decodePolyline(polyline)
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.clearDecodedRoutePoints()
        decodedPoints.forEach((point) => {
          // @ts-ignore - Method exists but TypeScript can't infer it
          self.addDecodedRoutePoint(point)
        })
      }
    },

    loadAllLocations: flow(function* () {
      self.isLoadingLocations = true
      try {
        const locations: HubLocation[] = yield getAllLocations()
        self.allLocations.clear()
        locations.forEach((location) => {
          self.allLocations.push(location)
        })
      } catch (error: any) {
        console.error("Failed to load locations:", error)
      } finally {
        self.isLoadingLocations = false
      }
    }),

    setShowAllLocations(show: boolean) {
      self.showAllLocations = show
    },

    transitionToOrderView() {
      // Immediately transition from all locations to order view
      if (self.showAllLocations) {
        self.showAllLocations = false
      }
    },

    refreshOrder() {
      if (self.trackingToken && !self.currentOrderId) {
        // Token-only refresh
        // @ts-ignore - Method exists but TypeScript can't infer it
        return self.loadOrderByToken(self.trackingToken)
      } else if (self.currentOrderId) {
        // Normal refresh with order ID
        // @ts-ignore - Method exists but TypeScript can't infer it
        return self.loadOrder(self.currentOrderId, self.trackingToken || undefined)
      }
    },

    scheduleDelayedResync() {
      // Clear any existing timeout
      if (self.delayedSyncTimeout) {
        clearTimeout(self.delayedSyncTimeout)
      }

      // Schedule resync after 60 seconds (1 minute)
      self.delayedSyncTimeout = setTimeout(() => {
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.silentRefreshOrderData()
      }, 60000) // 60 seconds (1 minute)
    },

    stopDelayedResync() {
      if (self.delayedSyncTimeout) {
        clearTimeout(self.delayedSyncTimeout)
        self.delayedSyncTimeout = null
      }
    },

    connectToPusher() {
      if (!self.currentOrderId || !self.trackingToken) {
        return
      }

      // Disconnect existing connection if any
      if (self.pusherService) {
        self.pusherService.disconnect()
      }

      // Connecting to Pusher for real-time updates

      // Create new Pusher service with tracking token
      // @ts-ignore - Method exists but TypeScript can't infer it
      self.setPusherService(new PusherService(self.trackingToken))

      // Subscribe to customer order channel
      self.pusherService?.subscribeToCustomerOrder(self.currentOrderId, {
        onLocationUpdate: (data: LocationUpdate) => {
          // Received driver location update

          // Update driver location - must be in action
          if (self.currentOrder) {
            const location = {
              latitude: data.latitude,
              longitude: data.longitude,
              timestamp: data.timestamp || Date.now(),
              heading: 0,
              speed: 0,
              isReturn: data.isReturn || false,
            }
            self.currentOrder.updateDriverLocation(location)
            // Updated driver location with return status
          }
          // @ts-ignore - Method exists but TypeScript can't infer it
          self.updateLastUpdateTime()
        },
        // Driver status handlers
        onDriverStarted: (data: DriverStatusUpdate) => {
          // Driver started journey
          if (self.currentOrder) {
            // If the "started" event includes initial GPS position, use it
            // to show the car immediately (before the first location-update arrives)
            const dataAny = data as any
            if (typeof dataAny.latitude === "number" && typeof dataAny.longitude === "number") {
              const location = {
                latitude: dataAny.latitude,
                longitude: dataAny.longitude,
                timestamp: data.timestamp || Date.now(),
                heading: 0,
                speed: 0,
                isReturn: data.isReturn ?? false,
              }
              self.currentOrder.updateDriverLocation(location)
              // @ts-ignore
              self.updateLastUpdateTime()
            } else if (data.isReturn !== undefined && self.currentOrder.currentDriverLocation) {
              // No position data — only update isReturn status
              const location = {
                ...self.currentOrder.currentDriverLocation,
                isReturn: data.isReturn,
              }
              self.currentOrder.updateDriverLocation(location)
            }
          }
        },
        onDriverArrived: (data: DriverStatusUpdate) => {
          // Driver arrived at destination
          // Update isReturn status when driver arrives
          if (self.currentOrder && data.isReturn !== undefined) {
            // Updating return status from arrival event
            // Only update isReturn status, don't change location
            if (self.currentOrder.currentDriverLocation) {
              const location = {
                ...self.currentOrder.currentDriverLocation,
                isReturn: data.isReturn,
              }
              self.currentOrder.updateDriverLocation(location)
            }
          }
          // Important: Do NOT disconnect or stop tracking here
          // The driver will continue sending location updates with isReturn: true
          // as they drive back to the hub
        },
        onDriverCancelled: (_data: DriverStatusUpdate) => {
          // Driver journey cancelled
          // Handle cancellation if needed
        },
        onETAUpdate: (data: LiveETAUpdate) => {
          // Drop only fully empty payloads. Pickup-only fan-outs from the
          // backend heartbeat carry projectedPickupAtLive without liveMinutes
          // (the return-leg minutes are meaningless during pickup decay) —
          // those must still propagate to the model so the displayed pickup
          // time advances during GPS silence.
          if (!self.currentOrder) return
          if (
            data.liveMinutes == null &&
            data.projectedCompletionAtLive == null &&
            data.projectedPickupAtLive == null
          ) {
            return
          }
          // Pass projectedCompletionAtLive and projectedPickupAtLive through
          // as-is. NEVER synthesize from data.eta — that fallback would
          // resurrect the live UI even when the backend live_return_eta /
          // live_pickup_eta flag has stripped the field, defeating the
          // server-side kill switch.
          self.currentOrder.updateLiveETA(
            data.liveMinutes,
            data.timestamp,
            data.projectedCompletionAtLive,
            data.projectedPickupAtLive,
          )
        },
        onStageUpdate: (data: StageUpdate) => {
          // Received order stage update

          // Update order status when stage changes - must be in action
          if (self.currentOrder && data.newStage) {
            // @ts-ignore - Method exists but TypeScript can't infer it
            self.updateOrderStatus(data.newStage)
          }

          // Update isReturn status if provided
          if (self.currentOrder && data.isReturn !== undefined) {
            // Updating return status from stage update
            // Only update isReturn status, don't change location
            if (self.currentOrder.currentDriverLocation) {
              const location = {
                ...self.currentOrder.currentDriverLocation,
                isReturn: data.isReturn,
              }
              self.currentOrder.updateDriverLocation(location)
            }
          }

          // Fetch fresh order data to get updated report/verdict fields
          // This is necessary because Pusher stage-update events don't include full order data
          // Use declared action instead of inline flow to maintain MST context and reactivity
          // @ts-ignore - Method exists but TypeScript can't infer it
          self.silentRefreshOrderData()

          // @ts-ignore - Method exists but TypeScript can't infer it
          self.updateLastUpdateTime()
        },
        onPickupRealized: (data) => {
          // PLA-25: realised handover broadcast. First-write-wins is enforced
          // on the MST model so a re-broadcast or duplicate event is a no-op.
          if (!self.currentOrder) return
          try {
            const iso = new Date(data.realisedPickupAt).toISOString()
            // @ts-ignore - Method exists but TypeScript can't infer it
            self.currentOrder.updateRealisedPickup(iso)
            // @ts-ignore - Method exists but TypeScript can't infer it
            self.updateLastUpdateTime()
          } catch (error) {
            console.error(`[Pusher] Error handling pickup-realized:`, error)
          }
        },
        onReportAvailable: () => {
          // PLA-111: backend fan-out when berichtPdfStatus flips into "available".
          // Bottom sheet observes hasPdfReport, so the download button enables
          // on the next render with no page reload.
          if (!self.currentOrder) return
          try {
            // @ts-ignore - Method exists but TypeScript can't infer it
            self.currentOrder.setHasPdfReport(true)
            // @ts-ignore - Method exists but TypeScript can't infer it
            self.updateLastUpdateTime()
          } catch (error) {
            console.error(`[Pusher] Error handling report-available:`, error)
          }
        },
      })

      // Update Pusher connection state
      // @ts-ignore - Method exists but TypeScript can't infer it
      self.setPusherConnected(true)
    },

    disconnectPusher() {
      if (self.pusherService) {
        // Disconnecting from Pusher
        self.pusherService.disconnect()
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.setPusherService(null)
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.setPusherConnected(false)
      }
    },

    // Countdown timer actions
    startCountdown() {
      console.log("Starting countdown timer")
      self.isCountdownActive = true
      self.countdownComplete = false
    },

    setCountdownComplete(complete: boolean) {
      self.countdownComplete = complete
      if (complete) {
        self.isCountdownActive = false
      }
    },

    stopCountdown() {
      self.isCountdownActive = false
      self.countdownComplete = false
    },

    setCameFromSuccessPage(value: boolean) {
      self.cameFromSuccessPage = value
    },

    resetState() {
      // Disconnect Pusher before resetting
      ;(self as any).disconnectPusher()

      // Clear polling interval if exists
      // @ts-ignore - Method exists but TypeScript can't infer it
      self.stopServiceTypePolling()

      // Clear sync retry interval if exists
      // @ts-ignore - Method exists but TypeScript can't infer it
      self.stopSyncRetrying()

      // Clear delayed resync timeout if exists
      // @ts-ignore - Method exists but TypeScript can't infer it
      self.stopDelayedResync()

      // Clear B2C action polling
      // @ts-ignore - Method exists but TypeScript can't infer it
      self.stopPostActionPolling()

      // Reset B2C modal states
      self.showB2cRescheduleModal = false
      self.showB2cCancelModal = false
      self.b2cPickerIntent = "reschedule"
      self.rescheduleSucceededB2c = false
      self.rescheduleRequestId += 1
      self.confirmedSlot = null
      self.isReschedulingB2c = false
      self.isCancellingB2c = false
      self.rescheduleErrorB2c = undefined
      self.rescheduleErrorCodeB2c = undefined
      self.cancelErrorB2c = undefined

      // Reset countdown states
      self.isCountdownActive = false
      self.countdownComplete = false

      // Reset success page flag
      self.cameFromSuccessPage = false

      // Reset all state properties
      self.currentOrder = undefined
      self.currentOrderId = undefined
      self.trackingToken = undefined
      // The order tag is Sentry scope state, not store state — clear it here
      // or every error after unmount keeps pointing at the order just left.
      syncSentryOrderTag(undefined)
      self.isLoading = false
      self.isSyncing = false
      self.retryAttempt = 0
      self.maxRetriesReached = false
      self.error = undefined
      self.lastUpdateTime = undefined
      // @ts-ignore - Method exists but TypeScript can't infer it
      self.clearDecodedRoutePoints()
      self.routePolyline = undefined
      self.routeDistance = undefined
      self.routeDuration = undefined
    },

    // Dedicated update actions for external callbacks
    updateLastUpdateTime() {
      self.lastUpdateTime = new Date()
    },

    setPusherService(service: PusherService | null) {
      self.pusherService = service
    },

    setPusherConnected(connected: boolean) {
      self.isPusherConnected = connected
    },

    setDriverOffRouteState(offRoute: boolean) {
      self.isDriverOffRoute = offRoute
    },

    showRescheduleModalAction() {
      self.showRescheduleModal = true
    },

    /**
     * PLA-882: no_show / cancelled orders. b2c-website sources rebook through the
     * new picker (once per visit, same guard as the widget popup); every other
     * source keeps the booking-widget popup.
     */
    openNoShowOrCancelledPicker(status: string, orderSource?: string | null) {
      const intent = pickerIntentForStatus(status, orderSource)
      if (!intent) {
        self.showRescheduleModal = true
        return
      }
      self.b2cPickerIntent = intent
      self.showB2cRescheduleModal = true
      self.rescheduleSucceededB2c = false
      self.rescheduleRequestId += 1
      self.confirmedSlot = null
      self.rescheduleErrorB2c = undefined
      self.rescheduleErrorCodeB2c = undefined
      self.hasShownNoShowModal = true
    },

    hideRescheduleModal() {
      self.showRescheduleModal = false
      self.hasShownNoShowModal = true
    },

    resetModalState() {
      self.showRescheduleModal = false
      self.hasShownNoShowModal = false
      self.showB2cRescheduleModal = false
      self.showB2cCancelModal = false
      self.b2cPickerIntent = "reschedule"
      self.rescheduleSucceededB2c = false
      self.rescheduleRequestId += 1
      self.confirmedSlot = null
      self.rescheduleErrorB2c = undefined
      self.rescheduleErrorCodeB2c = undefined
      self.cancelErrorB2c = undefined
    },

    // ─── B2C Reschedule/Cancel Actions ──────────────────────────

    showB2cRescheduleModalAction() {
      // A no_show / cancelled order reopens with its recovery intent (PLA-882).
      self.b2cPickerIntent =
        pickerIntentForStatus(self.currentOrder?.status, self.currentOrder?.orderSource) ??
        "reschedule"
      self.showB2cRescheduleModal = true
      self.rescheduleSucceededB2c = false
      self.rescheduleRequestId += 1
      self.confirmedSlot = null
      self.rescheduleErrorB2c = undefined
      self.rescheduleErrorCodeB2c = undefined
    },

    hideB2cRescheduleModal() {
      self.showB2cRescheduleModal = false
      self.rescheduleSucceededB2c = false
      self.rescheduleRequestId += 1
      self.confirmedSlot = null
      self.rescheduleErrorB2c = undefined
      self.rescheduleErrorCodeB2c = undefined
      self.cancelErrorB2c = undefined
    },

    // PLA-878: "Stornieren" opens the rebooking picker in cancel intent.
    showB2cCancelModalAction() {
      self.b2cPickerIntent = "cancel"
      self.showB2cRescheduleModal = true
      self.rescheduleSucceededB2c = false
      self.rescheduleRequestId += 1
      self.confirmedSlot = null
      self.rescheduleErrorB2c = undefined
      self.rescheduleErrorCodeB2c = undefined
      self.cancelErrorB2c = undefined
    },

    hideB2cCancelModal() {
      self.showB2cCancelModal = false
      self.cancelErrorB2c = undefined
    },

    /** Drops a stale reschedule error when the customer goes back to pick another time. */
    clearRescheduleErrorB2c() {
      self.rescheduleErrorB2c = undefined
      self.rescheduleErrorCodeB2c = undefined
    },

    rescheduleB2cOrder: flow(function* (data: { abholdatum: string; spatesteAbholung: string }) {
      if (!self.currentOrderId || !self.trackingToken) return

      self.isReschedulingB2c = true
      self.rescheduleInFlight += 1
      self.rescheduleErrorB2c = undefined
      self.rescheduleErrorCodeB2c = undefined
      // Bound to picker session + order: a stale completion must not touch the UI.
      const scope = startB2cMutation(self)

      try {
        yield rescheduleB2cOrderApi({
          orderId: self.currentOrderId,
          trackingToken: self.trackingToken,
          abholdatum: data.abholdatum,
          spatesteAbholung: data.spatesteAbholung,
        })
        // The ticket is rescheduled either way; the re-sync shows it.
        if (isCurrentB2cMutation(self, scope)) {
          // Modal stays open on its success screen; "Fertig" closes it (PLA-878).
          self.confirmedSlot = { ...data }
          self.rescheduleSucceededB2c = true
        }
        // The ticket is rescheduled either way; re-sync while we are still on that order.
        if (scope.orderId === self.currentOrderId) {
          // @ts-ignore
          self.startPostActionPolling()
        }
      } catch (error: any) {
        if (isCurrentB2cMutation(self, scope)) {
          self.rescheduleErrorB2c = error.message || "Fehler beim Verschieben"
          self.rescheduleErrorCodeB2c = typeof error.code === "string" ? error.code : undefined
        }
      } finally {
        self.rescheduleInFlight -= 1
        self.isReschedulingB2c = self.rescheduleInFlight > 0
      }
    }),

    cancelB2cOrder: flow(function* () {
      if (!self.currentOrderId || !self.trackingToken) return

      self.isCancellingB2c = true
      self.cancelInFlight += 1
      self.cancelErrorB2c = undefined
      const scope = startB2cMutation(self)

      try {
        yield cancelB2cOrderApi(scope.orderId as string, scope.token as string)
        // The order is cancelled either way; only the owning session closes its picker.
        if (isCurrentB2cMutation(self, scope)) {
          self.showB2cCancelModal = false
          self.showB2cRescheduleModal = false
        }
        if (scope.orderId === self.currentOrderId) {
          // @ts-ignore
          self.startPostActionPolling()
        }
      } catch (error: any) {
        if (isCurrentB2cMutation(self, scope)) {
          self.cancelErrorB2c = error.message || "Fehler beim Stornieren"
        }
      } finally {
        self.cancelInFlight -= 1
        self.isCancellingB2c = self.cancelInFlight > 0
      }
    }),

    _advancePollingAttempt() {
      self.b2cActionPollingAttempt += 1
    },

    _stopPolling() {
      self.isPollingAfterAction = false
    },

    _setPollingTimeout(handle: ReturnType<typeof setTimeout> | null) {
      self.b2cActionPollingInterval = handle
    },

    startPostActionPolling() {
      // @ts-ignore
      self.stopPostActionPolling()

      self.isPollingAfterAction = true
      self.b2cActionPollingAttempt = 0

      const intervals = [2000, 3000, 4000, 4000, 4000, 4000]

      const poll = () => {
        if (self.b2cActionPollingAttempt >= intervals.length) {
          // Mutations inside setTimeout are outside the MST action boundary —
          // call named actions so MobX strict mode is satisfied.
          ;(self as any)._stopPolling()
          return
        }

        // Silent refresh — call the named model action so MST flow context is valid
        // @ts-ignore - Method exists but TypeScript can't infer it
        self._refreshAfterAction()
        ;(self as any)._advancePollingAttempt()

        if (self.b2cActionPollingAttempt < intervals.length) {
          ;(self as any)._setPollingTimeout(
            setTimeout(poll, intervals[self.b2cActionPollingAttempt]),
          )
        } else {
          ;(self as any)._stopPolling()
        }
      }

      poll()
    },

    stopPostActionPolling() {
      if (self.b2cActionPollingInterval) {
        clearTimeout(self.b2cActionPollingInterval)
        self.b2cActionPollingInterval = null
      }
      self.isPollingAfterAction = false
      self.b2cActionPollingAttempt = 0
    },

    setConnectionStatusState(connected: boolean) {
      self.isConnected = connected
    },

    updateRouteDataState(polyline: string, distance: number, duration: string) {
      self.routePolyline = polyline
      self.routeDistance = distance
      self.routeDuration = duration
      self.lastRouteUpdate = new Date()
    },

    clearDecodedRoutePoints() {
      self.decodedRoutePoints.clear()
    },

    addDecodedRoutePoint(point: { latitude: number; longitude: number }) {
      self.decodedRoutePoints.push({
        latitude: point.latitude,
        longitude: point.longitude,
        timestamp: Date.now(),
        heading: 0,
        speed: 0,
      })
    },

    startServiceTypePolling() {
      // Clear any existing interval
      if (self.serviceTypePollingInterval) {
        clearInterval(self.serviceTypePollingInterval)
      }

      // Poll every 30 seconds for serviceType
      self.serviceTypePollingInterval = setInterval(() => {
        // Only poll if serviceType is still not defined
        if (self.currentOrder && !self.currentOrder.serviceType) {
          // Call the existing top-level model flow actions directly — they already
          // have proper MST action context, so no inline flow() wrapper is needed.
          const afterLoad = () => {
            if (self.currentOrder?.serviceType) {
              // @ts-ignore - Method exists but TypeScript can't infer it
              self.stopServiceTypePolling()
            }
          }
          try {
            if (self.trackingToken && !self.currentOrderId) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore - loadOrderByToken exists but TypeScript can't infer cross-action types
              ;(self as any)
                .loadOrderByToken(self.trackingToken)
                .then(afterLoad)
                .catch(() => {})
            } else if (self.currentOrderId) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore - loadOrder exists but TypeScript can't infer cross-action types
              ;(self as any)
                .loadOrder(self.currentOrderId, self.trackingToken || undefined)
                .then(afterLoad)
                .catch(() => {})
            }
          } catch {
            // Error polling for serviceType
          }
        } else if (self.currentOrder?.serviceType) {
          // ServiceType exists, stop polling
          // @ts-ignore - Method exists but TypeScript can't infer it
          self.stopServiceTypePolling()
        }
      }, 30000) // 30 seconds
    },

    stopServiceTypePolling() {
      if (self.serviceTypePollingInterval) {
        clearInterval(self.serviceTypePollingInterval)
        self.serviceTypePollingInterval = null
      }
    },

    // Action to handle order-created event from Pusher
    // This is called when the backend notifies us that the order is ready
    handleOrderCreated: flow(function* (_orderId: string) {
      try {
        // Stop retrying since order is now available
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.stopSyncRetrying()

        // Load the order immediately
        if (self.trackingToken) {
          // @ts-ignore - Method exists but TypeScript can't infer it
          yield self.loadOrderByToken(self.trackingToken)
        }
      } catch {
        // Failed to load after notification - resume polling as fallback
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.scheduleNextRetry()
      }
    }),

    // Separate action for retry attempt that can be called from setTimeout
    tryLoadOrderRetry: flow(function* () {
      try {
        self.retryAttempt += 1

        // Attempt to load the order
        if (self.trackingToken && !self.currentOrderId) {
          // @ts-ignore - Method exists but TypeScript can't infer it
          yield self.loadOrderByToken(self.trackingToken, true) // Pass isRetryAttempt = true
        } else if (self.currentOrderId) {
          // @ts-ignore - Method exists but TypeScript can't infer it
          yield self.loadOrder(self.currentOrderId, self.trackingToken || undefined)
        }

        // If successful and we have an order, stop retrying
        if (self.currentOrder) {
          // @ts-ignore - Method exists but TypeScript can't infer it
          self.stopSyncRetrying()
          return true
        }

        return false
      } catch {
        // Continue retrying on error
        return false
      }
    }),

    // Action to schedule the next retry
    // Polls every 3s for up to 4 minutes (~80 attempts) to cover the webhook delayed sync window
    scheduleNextRetry() {
      const MAX_RETRY_ATTEMPTS = 80
      if (self.retryAttempt >= MAX_RETRY_ATTEMPTS) {
        Sentry.captureException(
          new Error(`Sync retry exhausted after ${MAX_RETRY_ATTEMPTS} attempts`),
          {
            tags: { flow: "tracking", operation: "sync-retry-exhausted" },
            extra: {
              retryAttempt: self.retryAttempt,
              trackingToken: self.trackingToken ? "present" : "missing",
              orderId: self.currentOrderId || "unknown",
            },
          },
        )
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.stopSyncRetrying()
        self.maxRetriesReached = true
        return
      }

      // Consistent 3s polling — order creation via webhook takes up to 3-4 minutes
      const interval = 3000

      self.syncRetryInterval = setTimeout(() => {
        // Call the action that's properly wrapped in MST flow
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.tryLoadOrderRetry().then((success: boolean) => {
          if (!success && !self.maxRetriesReached) {
            // @ts-ignore - Method exists but TypeScript can't infer it
            self.scheduleNextRetry()
          }
        })
      }, interval)
    },

    startSyncRetrying: flow(function* () {
      // Clear any existing retry interval
      if (self.syncRetryInterval) {
        clearTimeout(self.syncRetryInterval)
      }

      self.isSyncing = true
      self.retryAttempt = 0
      self.maxRetriesReached = false
      self.error = undefined

      // Connect to Pusher immediately with tracking token to listen for order-created event
      // This allows real-time notification when order appears in database
      if (self.trackingToken && !self.pusherService) {
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.setPusherService(new PusherService(self.trackingToken))

        // Subscribe to tracking-token channel for order creation notifications
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.pusherService?.subscribeToTrackingToken(self.trackingToken, (data: any) => {
          // Order created! Use MST action to handle it properly
          if (data.orderId) {
            // @ts-ignore - Method exists but TypeScript can't infer it
            self.handleOrderCreated(data.orderId)
          }
        })
      }

      // Initial attempt
      // @ts-ignore - Method exists but TypeScript can't infer it
      const initialSuccess = yield self.tryLoadOrderRetry()
      if (initialSuccess) return

      // Set up retry intervals based on attempt count
      // @ts-ignore - Method exists but TypeScript can't infer it
      self.scheduleNextRetry()
    }),

    stopSyncRetrying() {
      if (self.syncRetryInterval) {
        clearTimeout(self.syncRetryInterval)
        self.syncRetryInterval = null
      }
      self.isSyncing = false
    },

    reloadPage() {
      // For web, reload the page
      if (typeof window !== "undefined" && window.location) {
        window.location.reload()
      } else {
        // For mobile, reset and restart the sync process
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.resetState()
        // @ts-ignore - Method exists but TypeScript can't infer it
        self.startSyncRetrying()
      }
    },
  }))

export interface OrderTrackingStore extends Instance<typeof OrderTrackingStoreModel> {}
export interface OrderTrackingStoreSnapshot extends SnapshotOut<typeof OrderTrackingStoreModel> {}
